import React from 'react';
import {
  Action,
  ActionIcon,
  FontWeight,
  FormHandlers,
  ICON_EYE_OUTLINE,
  ModalSize,
  Portal,
  Spacing,
  Text,
  useModal,
} from '@doveit/bricks';
import { ActionIconSize } from '@doveit/bricks/dist/typings/components/action-icon/types';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import UpdateFeedback, { UpdateFeedbackProps } from '../update-feedback/UpdateFeedback';
import { Feedback } from '../../../providers/api/dtos';
import ViewProspectPreview from '../../../prospect/containers/view-prospect-preview/ViewProspectPreview';
import useContact from '../../../contact/hooks/use-contact/useContact';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { FeedbackStatus, ReferenceType } from '../../../domain/types';

export interface ViewFeedbackActionProps extends Pick<UpdateFeedbackProps, 'feedback' | 'onSuccess' | 'disabled'> {
  canEdit?: boolean,
  newStatus?: FeedbackStatus,
  size?: ActionIconSize,
  children?: ({ disabled, view }: { disabled?: boolean, view: VoidFunction }) => React.ReactNode
}

export const ViewFeedbackAction: React.FunctionComponent<ViewFeedbackActionProps> = ({
  feedback,
  canEdit = false,
  disabled,
  newStatus,
  size,
  onSuccess,
  children,
}) => {
  const { data: contact } = useContact(feedback.contactId);
  const [isSaving, setIsSaving] = React.useState(false);
  const feedbackModal = useModal<Feedback>();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const onInternalSuccess = React.useCallback((updatedFeedback: Feedback) => {
    setIsSaving(false);
    onSuccess?.(updatedFeedback);
    feedbackModal.close();
  }, [feedbackModal, onSuccess]);

  const onError = React.useCallback(() => {
    setIsSaving(false);
  }, []);

  const onActionClick = React.useCallback(() => {
    feedbackModal.open();
  }, [feedbackModal]);

  const submitForm = React.useCallback(() => {
    setIsSaving(true);
    formRef.current?.handleSubmit();
  }, []);

  return (
    <>
      {children
        ? children({ disabled, view: onActionClick })
        : (
          <ActionIcon
            label="Vedi"
            icon={{ path: ICON_EYE_OUTLINE }}
            disabled={disabled}
            onClick={onActionClick}
            aria-label="Pulsante per visualizzare la survey"
            size={size}
          />
        )}
      <Portal>
        <SimpleModal
          {...feedbackModal}
          title={feedback.survey?.name ? `Survey: "${feedback.survey.name}"` : 'Survey'}
          size={ModalSize.MEDIUM}
          aria-label="Modale per visualizzare la survey"
          footer={canEdit ? (
            <Action
              label={newStatus === FeedbackStatus.REVIEWED ? 'Segna come revisionata' : 'Salva survey'}
              color="primary"
              emphasis="high"
              loading={isSaving}
              aria-label="Pulsante per salvare la survey"
              onClick={submitForm}
            />
          ) : undefined}
        >
          <Text.Body
            fontWeight={FontWeight.LIGHT}
            color="neutral.default.low"
          >
            Intervista a <strong>{contact?.name || NO_VALUE_SYMBOL}</strong> in merito a:
          </Text.Body>
          <Spacing margin={[100, 0, 0]} />

          {feedback.reference?.type === ReferenceType.PROSPECT && (
            <ViewProspectPreview prospectId={feedback.reference.id} />
          )}

          <Spacing margin={[400, 0, 0]} />
          <UpdateFeedback
            feedback={feedback}
            newStatus={newStatus}
            onSuccess={onInternalSuccess}
            onError={onError}
            formRef={formRef}
            disabled={!canEdit}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};
